'use client';

import { EventCard } from '@/components/EventCard';
import { FilterControls, FilterState } from '@/components/FilterControls';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { mockEvents, Event } from '@/lib/mock-data';
import { useState, useMemo } from 'react';
import { Calendar, MapPin, Sparkles } from 'lucide-react';

export default function EventsPage() {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    country: '',
    sortBy: 'date',
  });
  const [isLoading] = useState(false);

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let results = [...mockEvents];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(
        (event) =>
          event.artistName.toLowerCase().includes(searchLower) ||
          event.eventTitle.toLowerCase().includes(searchLower) ||
          event.city.toLowerCase().includes(searchLower)
      );
    }

    // Apply country filter
    if (filters.country) {
      results = results.filter((event) => event.country === filters.country);
    }

    // Apply sorting
    results.sort((a, b) => {
      switch (filters.sortBy) {
        case 'date':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'artist':
          return a.artistName.localeCompare(b.artistName);
        case 'availability':
          return a.ticketsRemaining - b.ticketsRemaining;
        default:
          return 0;
      }
    });

    return results;
  }, [filters]);

  // Statistics
  const stats = useMemo(() => {
    const totalEvents = filteredEvents.length;
    const countries = new Set(filteredEvents.map((e) => e.country)).size;
    const lowAvailability = filteredEvents.filter((e) => e.ticketsRemaining < 10).length;

    return { totalEvents, countries, lowAvailability };
  }, [filteredEvents]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
        <div className="mx-auto max-w-7xl">
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
      <div className="mx-auto max-w-7xl">
        {/* Page Header */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 font-serif text-5xl font-bold text-white md:text-6xl">
            Upcoming{' '}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
              VIP Events
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-400">
            Discover exclusive VIP concert experiences across South Asia. Premium seats, luxury amenities, and unforgettable memories await.
          </p>

          {/* Quick Stats */}
          <div className="mt-8 flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-white">
                <strong className="text-[#D4AF37]">{stats.totalEvents}</strong> Events
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-white">
                <strong className="text-[#D4AF37]">{stats.countries}</strong> Countries
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-white">
                <strong className="text-[#D4AF37]">{stats.lowAvailability}</strong> Limited Availability
              </span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <FilterControls onFilterChange={setFilters} />

        {/* Event Grid */}
        {filteredEvents.length === 0 ? (
          <EmptyState
            icon="search"
            title="No Events Found"
            description="We couldn't find any events matching your filters. Try adjusting your search criteria or browse all events."
            actionLabel="View All Events"
            actionHref="/events"
          />
        ) : (
          <>
            <div className="mb-6 text-sm text-gray-400">
              Showing <strong className="text-white">{filteredEvents.length}</strong> event
              {filteredEvents.length !== 1 ? 's' : ''}
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {filteredEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
